import type { AnalysisRun, AnalysisSchedule, RunStatus } from '../models/impact.types'

/** A list row's status: a recurring schedule is `active`/`paused`, a one-time one is its run's. */
export type AnalysisListStatus = RunStatus | 'active' | 'paused'

/** Legacy `AnalysesManagement.vue` type filter — `recurring` covers daily, weekly and monthly. */
export type AnalysisTypeFilter = 'all' | 'one_time' | 'recurring'

export interface AnalysisListItem {
  schedule: AnalysisSchedule
  /** This schedule's runs, newest first. */
  runs: AnalysisRun[]
  latestRun: AnalysisRun | null
  status: AnalysisListStatus
}

export interface AnalysisListFilters {
  query: string
  type: AnalysisTypeFilter
  /** Legacy `selectedStatuses`: an empty selection means "all statuses", not none. */
  statuses: readonly string[]
}

export function isRecurring(schedule: AnalysisSchedule): boolean {
  return schedule.frequency !== 'one_time'
}

/**
 * Legacy `deriveOneTimeStatus`/`is_active` branch: a recurring schedule's status is ALWAYS its
 * active/paused state, regardless of run history — checking the latest run first misclassifies a
 * paused-but-previously-run schedule as "completed" and makes it unreachable via the active/paused
 * filter. A one-time schedule with no run yet is `pending`.
 */
export function scheduleStatus(
  schedule: AnalysisSchedule,
  latestRun: AnalysisRun | null,
): AnalysisListStatus {
  if (isRecurring(schedule)) return schedule.is_active ? 'active' : 'paused'
  return latestRun?.status ?? 'pending'
}

/** A schedule's runs matched on `schedule_id`, newest `created_at` first. */
export function runsForSchedule(runs: readonly AnalysisRun[], scheduleId: number): AnalysisRun[] {
  return runs
    .filter((run) => run.schedule_id === scheduleId)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
}

/**
 * Legacy `items` (`AnalysesManagement.vue`): one row per schedule, sorted by schedule `id`
 * descending — the most recently *created* schedule leads, not the one with the most recent run.
 * `runsOf` returns a schedule's runs newest-first (`impactStore.runsBySchedule`).
 */
export function toItems(
  schedules: readonly AnalysisSchedule[],
  runsOf: (scheduleId: number) => AnalysisRun[],
): AnalysisListItem[] {
  return [...schedules]
    .sort((a, b) => b.id - a.id)
    .map((schedule) => {
      const runs = runsOf(schedule.id)
      const latestRun = runs[0] ?? null
      return { schedule, runs, latestRun, status: scheduleStatus(schedule, latestRun) }
    })
}

/** Legacy `filteredData`: name search, type filter and status filter, all combined with AND. */
export function filterItems(
  items: readonly AnalysisListItem[],
  filters: AnalysisListFilters,
): AnalysisListItem[] {
  const query = filters.query.trim().toLowerCase()
  return items.filter(({ schedule, status }) => {
    const typeMatches =
      filters.type === 'all' ||
      (filters.type === 'recurring' ? isRecurring(schedule) : schedule.frequency === filters.type)
    const statusMatches = filters.statuses.length === 0 || filters.statuses.includes(status)
    return typeMatches && statusMatches && (!query || schedule.name.toLowerCase().includes(query))
  })
}
